import type { ReactNode } from 'react';
import { UserMenu } from './UserMenu';

/**
 * App chrome: a slim top bar with the product name, plus a centered content
 * container. Every page renders inside this shell.
 *
 * Kept intentionally minimal — the wizard owns its own progress + step chrome
 * below this bar.
 *
 * Container width is max-w-7xl (1280px): wide enough for the dashboard's
 * table-heavy views (Customers/Subscriptions/Products/Audit) to breathe, while
 * still capping content on ultrawide monitors. The onboarding wizard stays
 * readable because its inner cards self-center at narrower widths
 * (max-w-xl / max-w-2xl with mx-auto) inside this wider shell.
 */
export function AppShell({ children }: { children: ReactNode; }) {
  return (
    <div className="min-h-screen">
      <header className="border-b border-ink-200 bg-white">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <Logo />
            <span className="text-sm font-semibold tracking-tight text-ink-800">
              Stripe Sync
            </span>
            <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[11px] font-medium text-brand-600">
              by AmpDECK
            </span>
          </div>
          <div className="flex items-center gap-4">
            <UserMenu />
            <a
              href="https://github.com/AmpDECK"
              target="_blank"
              rel="noreferrer"
              className="text-xs font-medium text-ink-400 transition-colors hover:text-ink-600"
            >
              Help
            </a>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8">{children}</main>
    </div>
  );
}

function Logo() {
  // Inline SVG — no extra dep. Two interlocking strokes suggest the
  // Stripe↔HubSpot sync loop.
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect width="24" height="24" rx="6" fill="#4f46e5" />
      <path
        d="M7 8.5h6a3.5 3.5 0 0 1 0 7H7v-7Z"
        stroke="#fff"
        strokeWidth="1.6"
      />
      <circle cx="14.5" cy="12" r="1.4" fill="#fff" />
    </svg>
  );
}
